/** One suggestible colour already in play elsewhere in the theme. */
export interface ColorSuggestion {
  /** Where it comes from, e.g. "Accent" — used as the chip's label. */
  label: string;
  /** Normalised #rrggbb. */
  hex: string;
}

export interface ResolvedColorSuggestion extends ColorSuggestion {
  /** True when the row being edited already holds this colour. */
  current: boolean;
}

/**
 * Resolve the theme's own colours into the list shown under one colour row,
 * where `current` is that row's present value.
 *
 * Two rules:
 *
 *  - **Deduped by value, not label.** Two knobs commonly hold the same colour
 *    (a heading colour still matching body ink, an accent reused for headings),
 *    and rendering one swatch twice gives two identical-looking entries that do
 *    exactly the same thing. The first wins, so callers should order the input
 *    most-canonical first.
 *  - **The current value is kept and flagged**, not hidden. Seeing "Body text ✓"
 *    when opening the Headings picker is how the user learns the two are still
 *    tied together — dropping it would make a deliberate match look like an
 *    absence. Flagged entries are inert in the UI: there is nothing to apply.
 *
 * `self` is the label of the row being edited. Its palette entry is skipped
 * because "Headings" naming itself on the Headings row is circular noise; the
 * tick should land on whatever OTHER source shares the value.
 */
export function buildColorSuggestions(
  all: ColorSuggestion[],
  current: string,
  self?: string,
): ResolvedColorSuggestion[] {
  const currentKey = current.toLowerCase();
  const seen = new Set<string>();
  const out: ResolvedColorSuggestion[] = [];

  for (const s of all) {
    if (self !== undefined && s.label === self) continue;
    const key = s.hex.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ ...s, current: key === currentKey });
  }

  return out;
}
